<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed } from 'vue'
import { setStoreLogger } from '../../logger.ts'
import { Grid, type GridSortChangeEvent, type GridPageChangeEvent } from '@progress/kendo-vue-grid'
import { EventOpenGetList } from '../../services/EventService.ts'
import type { IEvent } from '../../types/EventTypes'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { orderBy } from '@progress/kendo-data-query'
import { formatDateWithShortMonthAndTime, formatDateAsDDMMYYYY } from '../../api/Common.ts'
import { GetEventManagementUrl, GetEventListUrl } from '../../stores/SettingsStore.ts'
import { handleError } from '../../services/ErrorService.ts'
import { plusIcon } from '@progress/kendo-svg-icons'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { resolveColumnWidth } from '../../utils/kendoHelper.ts'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('HostEventList.vue')

const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'eventStartDate', dir: 'desc' }])
const gridData = ref<IEvent[]>([])
const sortedGridData = computed<IEvent[]>(() =>
  orderBy(gridData.value, sort.value),
)
const skip = ref(0);
const take = ref(15);
const pageSortedGridData = computed(() => sortedGridData.value.slice(skip.value, take.value + skip.value));
const total = computed(() => (gridData.value ? gridData.value.length : 0));
const columns = [
  {
    field: 'title',
    title: t('eventList.EventTitle'),
    width: resolveColumnWidth('250px', t('eventList.EventTitle')),
    cell: 'eventTitleTemplate',
  },
  {
    field: 'eventStartDate',
    title: t('eventList.EventDate'),
    width: resolveColumnWidth('150px', t('eventList.EventDate')),
    cell: 'eventDateTemplate',
  },
  {
    field: 'countdown',
    title: t('dashboard.openParties.DaysTilParty'),
    width: resolveColumnWidth('150px', t('dashboard.openParties.DaysTilParty')),
    cell: 'countdownTemplate',
  },
  {
    field: 'numberOfCarts',
    title: t('dashboard.openParties.NumberOfCarts'),
    width: resolveColumnWidth('120px', t('dashboard.openParties.NumberOfCarts')),
  },
  {
    field: 'numberOfOrders',
    title: t('dashboard.openParties.ExternalOrders'),
    width: resolveColumnWidth('120px', t('dashboard.openParties.ExternalOrders')),
  }
]

const eventListURL = ref<string>('')
const eventManagementURL = ref<string>('')

// Mobile pagination
const currentPage = ref(1)
const itemsPerPage = ref(5)
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return sortedGridData.value.slice(start, end)
})
const totalPages = computed(() => Math.ceil(sortedGridData.value.length / itemsPerPage.value))

function goToPage(page: number) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    // Scroll to top of mobile card view
    const mobileView = document.querySelector('.mobile-card-view')
    if (mobileView) {
      mobileView.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}

function nextPage() {
  goToPage(currentPage.value + 1)
}

function previousPage() {
  goToPage(currentPage.value - 1)
}

onMounted(async () => {
  logger.debug('onMounted')

  GetEventManagementUrl().then(url =>
  {
    eventManagementURL.value = url + "?event="
  })

  eventListURL.value = await GetEventListUrl()

  GetOpenEventList()
})

function sortChangeHandler(e: GridSortChangeEvent) {
  loader.value = true
  if (e.sort[0] !== undefined) {
    sort.value = e.sort
  } else {
    logger.debug('sortChangeHandler: e.sort[0] is undefined')
  }
  loader.value = false
  HideNoRecordsWhileLoading()
}

function pageChangeHandler(event: GridPageChangeEvent) {
  loader.value = true
  setTimeout(() => {
    loader.value = false
    skip.value = event.page.skip
    take.value = event.page.take
  }, 300)
}

function ReLoadGrid() {
  loader.value = true
  gridData.value = []
  sort.value = [{ field: 'eventStartDate', dir: 'desc' }]
  HideNoRecordsWhileLoading()
}

function HideNoRecordsWhileLoading() {
  const eventListDiv = document.getElementById(
    'eventListGridRowDiv',
  ) as HTMLElement
  const classAttribute = eventListDiv.attributes.getNamedItem('class')
  if (classAttribute) {
    if (loader.value == false) {
      classAttribute.value = 'row mt-3 desktop-grid-view'
    } else {
      classAttribute.value = 'row mt-3 grid-loading'
    }
  }
}

function GetOpenEventList() {
  ReLoadGrid()
  EventOpenGetList()
    .then(eventResp => {
      if (eventResp) {
        for (const event of eventResp.items) {
          gridData.value.push(event)
        }
        loader.value = false
        HideNoRecordsWhileLoading()
      }
    })
    .catch(async error => {
      await handleError(error, t)
    })
}

function createNewParty() {
  if (eventListURL.value) {
    window.location.href = eventListURL.value + "?isNewEvent=true"
  }
}

function calculateDaysUntilEvent(eventDate: Date | string): string {
  const today = new Date();
  const eventDateCopy = new Date(eventDate);
  if (!eventDate || eventDateCopy < today) {
    return '--';
  }

  today.setHours(0, 0, 0, 0); // Reset time part to get accurate day difference

  eventDateCopy.setHours(0, 0, 0, 0); // Reset time part to get accurate day difference

  // Calculate the difference in milliseconds
  const diffTime = eventDateCopy.getTime() - today.getTime();

  // Convert to days
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays === 1 ? '1 ' + t('dashboard.openParties.day') : `${diffDays} ${t('dashboard.openParties.days')}`;
}
</script>

<template>
  <div class="open-parties-container">
    <div class="parties-header">
      <div class="parties-title-section">
        <h2 class="pageHeader">{{ t('dashboard.openParties.title') }} <span class="order-count">{{ sortedGridData.length }} {{ t('dashboard.openParties.parties') }}</span></h2>
        <p class="pageSubSubHeader">{{ t('dashboard.openParties.description') }}</p>
      </div>
      <div class="create-party-button-container">
        <KButton
          :theme-color="'checkoutContinue'"
          class="baseBtn primaryBtn dashboard-open-parties-create-btn"
          @click="createNewParty"
          :svg-icon="plusIcon"
        >{{ t('dashboard.openParties.CreateParty') }}</KButton>
      </div>
    </div>

    <!-- Desktop Grid View -->
    <div id="eventListGridRowDiv" class="row mt-3 desktop-grid-view">
      <div class="col-12">
        <Grid
          ref="grid"
          :data-items="pageSortedGridData"
            :resizable="true"
            :sortable="true"
            :sort="sort"
            :pageable="true"
            :skip="skip"
          :take="take"
          :total="total"
            :columns="columns"
            @sortchange="sortChangeHandler"
            @pagechange="pageChangeHandler"
            :loader="loader"
          >
            <template v-slot:eventTitleTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="eventTitleDiv">
                <a class="event-title-link base-hyper-link" :href="eventManagementURL + props.dataItem.eventIdToken">{{ props.dataItem.title }}</a>
              </td>
            </template>
            <template v-slot:eventDateTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-center">
                {{
                  formatDateAsDDMMYYYY(props.dataItem.eventStartDate)
                }}
              </td>
            </template>
            <template v-slot:countdownTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'">
                {{ calculateDaysUntilEvent(props.dataItem.eventStartDate) }}
              </td>
            </template>
            <template v-slot:numberOfCartsTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'">
                {{ props.dataItem.numberOfCarts || 0 }}
              </td>
            </template>
            <template v-slot:externalOrdersTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'">
                {{ props.dataItem.externalOrders || 0 }}
              </td>
            </template>
          </Grid>
        </div>
      </div>

    <!-- Mobile Card View -->
    <div class="mobile-card-view">
      <div v-if="loader" class="loading-message">{{ t('labels.Loading') }}</div>
      <div v-else-if="sortedGridData.length === 0" class="no-parties-message">
        {{ t('dashboard.openParties.noParties') }}
      </div>
      <div v-else class="parties-cards-container">
        <div v-for="event in paginatedData" :key="event.eventIdToken" class="party-card">
          <a :href="eventManagementURL + event.eventIdToken" class="party-card-link">
            <div class="party-card-header">
              <h3 class="party-card-title">{{ event.title }}</h3>
            </div>
            <div class="party-card-body">
              <div class="party-card-row">
                <span class="party-card-label">{{ t('eventList.EventDate') }}:</span>
                <span class="party-card-value">{{ formatDateAsDDMMYYYY(event.eventStartDate) }}</span>
              </div>
              <div class="party-card-row">
                <span class="party-card-label">{{ t('dashboard.openParties.DaysTilParty') }}:</span>
                <span class="party-card-value">{{ calculateDaysUntilEvent(event.eventStartDate) }}</span>
              </div>
              <div class="party-card-row">
                <span class="party-card-label">{{ t('dashboard.openParties.NumberOfCarts') }}:</span>
                <span class="party-card-value">{{ event.numberOfCarts || 0 }}</span>
              </div>
              <div class="party-card-row">
                <span class="party-card-label">{{ t('dashboard.openParties.ExternalOrders') }}:</span>
                <span class="party-card-value">{{ event.numberOfOrders || 0 }}</span>
              </div>
            </div>
          </a>
        </div>

        <!-- Mobile Pagination Controls -->
        <div v-if="totalPages > 1" class="mobile-pagination">
          <div class="pagination-info">
            {{ t('rma.Showing') }} {{ (currentPage - 1) * itemsPerPage + 1 }}-{{ Math.min(currentPage * itemsPerPage, sortedGridData.length) }} {{ t('rma.Of') }} {{ sortedGridData.length }}
          </div>
          <div class="pagination-controls">
            <button
              :disabled="currentPage === 1"
              @click="goToPage(1)"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.FirstPage')"
            >
              &laquo;
            </button>
            <button
              :disabled="currentPage === 1"
              @click="previousPage"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.PreviousPage')"
            >
              &lt;
            </button>
            <div class="pagination-current">
              {{ currentPage }} {{ t('rma.Of') }} {{ totalPages }}
            </div>
            <button
              :disabled="currentPage === totalPages"
              @click="nextPage"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.NextPage')"
            >
              &gt;
            </button>
            <button
              :disabled="currentPage === totalPages"
              @click="goToPage(totalPages)"
              class="pagination-page-btn pagination-nav-btn"
              :title="t('rma.LastPage')"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.open-parties-container {
  padding: $spacing-2 $spacing-4;
  background-color: var(--vt-c-white);
  border-radius: $radius-md;
  box-shadow: 0 10px 15px rgba(black, 0.1);
}

.parties-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  gap: $spacing-5;
  flex-wrap: wrap;
}

.dashboard-open-parties-create-btn {
  padding: $spacing-2-5;
  margin: var(--base-spacing) 30px 0 var(--base-half-spacing);
  display: flex !important;
  white-space: nowrap;
}

.dashboard-open-parties-create-btn .k-icon {
  margin-right: 10px;
  line-height: 20px;
  height: 20px;
}

.dashboard-open-parties-create-btn .k-button-text {
  line-height: 20px;
  height: 20px;
}

.parties-title-section {
  margin-bottom: 15px;
  flex: 1;
  min-width: 250px;
}

.create-party-button-container {
  margin-bottom: 15px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
}

.order-count {
  @include typography-small;
  @include font-weight-regular;
  color: #666;
  background-color: #e9f5f7;
  padding: $spacing-1 $spacing-2;
  border-radius: $radius-lg;
  margin-left: 10px;
}

.grid-loading .k-grid-norecords .k-table-td {
  content-visibility: hidden;
  height: 50px;
}

/* Mobile Card View Styles */
.mobile-card-view {
  display: none !important;
}

.mobile-pagination {
  margin-top: $spacing-6;
  padding-top: $spacing-4;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  @include typography-small;
  color: #666;
  text-align: center;
  margin-bottom: $spacing-4;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2;
  flex-wrap: wrap;
}

.pagination-current {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333;
}

.pagination-pages {
  display: flex;
  gap: $spacing-2;
  flex-wrap: wrap;
  justify-content: center;
}

.pagination-page-btn {
  @include typography-small;
  @include font-weight-medium;
  padding: $spacing-2 $spacing-3;
  min-width: 36px;
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-page-btn:hover {
  background-color: var(--vt-c-white-soft);
  border-color: var(--vt-c-teal-dark);
}

.pagination-page-btn.active {
  background-color: var(--vt-c-teal-dark);
  color: var(--vt-c-white);
  border-color: var(--vt-c-teal-dark);
}

.pagination-page-btn.ellipsis {
  cursor: default;
  pointer-events: none;
  border-color: transparent;
  background: transparent;
}

.pagination-page-btn.ellipsis:hover {
  background: transparent;
  border-color: transparent;
}

.pagination-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.desktop-grid-view {
  display: block !important;
}

.parties-cards-container {
  display: flex;
  flex-direction: column;
  gap: $spacing-4;
}

.party-card {
  background: var(--vt-c-white);
  border: 1px solid #e0e0e0;
  border-radius: $radius-md;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.party-card:hover {
  box-shadow: 0 4px 12px rgba(black, 0.1);
  transform: translateY(-2px);
}

.party-card-link {
  text-decoration: none;
  color: inherit;
  display: block;
}

.party-card-header {
  padding: $spacing-4;
  background-color: var(--vt-c-white-soft);
  border-bottom: 1px solid #e0e0e0;
}

.party-card-title {
  @include typography-body-large;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.party-card-body {
  padding: $spacing-4;
}

.party-card-row {
  display: flex;
  justify-content: space-between;
  padding: $spacing-2 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.party-card-row:last-child {
  border-bottom: none;
}

.party-card-label {
  @include typography-small;
  @include font-weight-medium;
  color: #666;
}

.party-card-value {
  @include typography-small;
  @include font-weight-regular;
  color: #333;
  text-align: right;
}

.loading-message,
.no-parties-message {
  text-align: center;
  @include typography-body;
  padding: $spacing-10 $spacing-5;
  color: #666;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .open-parties-container {
    padding: $spacing-4;
  }

  .parties-header {
    flex-direction: column;
    align-items: stretch;
    gap: $spacing-4;
  }

  .parties-title-section {
    width: 100%;
    margin-bottom: 10px;
  }

  .parties-title-section .pageHeader {
    @include typography-h4;
  }

  .parties-title-section .pageSubSubHeader {
    @include typography-small;
  }

  .create-party-button-container {
    width: 100%;
    justify-content: stretch;
  }

  .dashboard-open-parties-create-btn {
    width: 100%;
    margin: 0;
    position: static;
    justify-content: center;
  }

  .order-count {
    display: block;
    margin-left: 0;
    margin-top: 8px;
    display: inline-block;
  }

  /* Hide desktop grid, show mobile cards */
  .desktop-grid-view {
    display: none !important;
  }

  .mobile-card-view {
    display: block !important;
  }
}
</style>
